import React, { createContext, useState, useContext, useRef, useEffect } from 'react';
import { createClient, webAuth } from '../api/client';

const AuthContext = createContext();

const SESSION_KEY = 'mvirs_session';

export const AuthProvider = ({ children }) => {
  const [webUser, setWebUser] = useState(null);       // web account (layer 1)
  const [mvirsUser, setMvirsUser] = useState(null);   // MVIRS user (layer 2)
  const [sessionLoading, setSessionLoading] = useState(true);
  const apiClientRef = useRef(null);

  // Restore session on app load
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const stored = localStorage.getItem(SESSION_KEY);
        if (!stored) { setSessionLoading(false); return; }

        const { sessionToken, expiresAt } = JSON.parse(stored);
        if (new Date(expiresAt) < new Date()) {
          localStorage.removeItem(SESSION_KEY);
          setSessionLoading(false);
          return;
        }

        const res = await webAuth.verifySession(sessionToken);
        if (res.data.valid) {
          setWebUser({ ...res.data.user, sessionToken });
        } else {
          localStorage.removeItem(SESSION_KEY);
        }
      } catch {
        localStorage.removeItem(SESSION_KEY);
      } finally {
        setSessionLoading(false);
      }
    };
    restoreSession();
  }, []);

  // Layer 1: Web account login
  const webLogin = async (username, password) => {
    const res = await webAuth.login(username, password);
    const { sessionToken, expiresAt, user } = res.data;

    localStorage.setItem(SESSION_KEY, JSON.stringify({ sessionToken, expiresAt }));
    setWebUser({ ...user, sessionToken });
    return user;
  };

  // Layer 2: MVIRS login (called from MvirsLoginScreen)
  const mvirsLogin = async (mvirsUserId, mvirsPassword) => {
    if (!webUser?.apiUrl) throw new Error('No shop API URL.');

    const tempClient = createClient(webUser.apiUrl, null);
    const response = await tempClient.post('/api/auth/login', {
      userId: mvirsUserId,
      password: mvirsPassword,
    });

    const { token, fullName, empId, isAdmin } = response.data;
    apiClientRef.current = createClient(webUser.apiUrl, token);
    setMvirsUser({ userId: mvirsUserId, fullName, empId, isAdmin, token });

    return response.data;
  };

  // Get list of MVIRS users for dropdown
  const getMvirsUsers = async () => {
    if (!webUser?.apiUrl) return [];
    try {
      const tempClient = createClient(webUser.apiUrl, null);
      const res = await tempClient.get('/api/auth/users');
      return res.data || [];
    } catch {
      return [];
    }
  };

  const logout = async () => {
    try {
      if (webUser?.sessionToken) {
        await webAuth.logout(webUser.sessionToken);
      }
    } catch {}
    localStorage.removeItem(SESSION_KEY);
    setWebUser(null);
    setMvirsUser(null);
    apiClientRef.current = null;
  };

  const logoutMvirs = () => {
    setMvirsUser(null);
    apiClientRef.current = null;
  };

  const getClient = () => apiClientRef.current;

  // Combined user object for components that need both layers
  const user = mvirsUser ? {
    ...mvirsUser,
    shopId: webUser?.shopId,
    shopName: webUser?.shopName,
    webUsername: webUser?.username,
  } : null;

  return (
    <AuthContext.Provider value={{
      webUser,
      mvirsUser,
      user,
      sessionLoading,
      webLogin,
      mvirsLogin,
      getMvirsUsers,
      logout,
      logoutMvirs,
      getClient,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);